import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  take,
} from 'rxjs/operators';
import { NOT_AVAILABLE } from 'src/app/shared/constants/display';
import { SharedModule } from 'src/app/shared/shared.module';
import { objectContainsTerm } from 'src/app/shared/utils/object-search.util';

import { CSVFormatterService } from 'src/app/core/services/csv-formatter';
import {
  DisplayOrder,
  Order,
  OrderStatus,
  OrderDetailViews as views,
} from 'src/app/models/order.model';
import * as fromStore from 'src/app/store/';

import { AsyncPipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { FocusContentOnOpenDirective } from 'src/app/shared/directives/focus-content-on-open.directive';
import { NgxPaginationModule } from 'ngx-pagination';
import { OrdersComponent } from 'src/app/orders/pages/orders-table/orders-table.component';
import type { OrderCsvDataRow } from '../../domain';
import { OrdersModule } from '../../orders.module';
import { OrderDetailPanelComponent } from '../order-detail-panel/order-detail-panel.component';
import { OrdersViewToggleComponent } from './orders-view-toggle/orders-view-toggle.component';

@Component({
  selector: 'app-orders-page',
  templateUrl: 'orders-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    OrderDetailPanelComponent,
    NgxPaginationModule,
    OrdersComponent,
    OrdersModule,
    SharedModule,
    MatExpansionModule,
    FocusContentOnOpenDirective,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatIconModule,
    OrdersViewToggleComponent,
  ],
})
export class OrdersPageComponent implements OnInit {
  private store = inject(Store<fromStore.State>);

  private route = inject(ActivatedRoute);

  private router = inject(Router);

  private csvFormatter = inject(CSVFormatterService);

  private ordersStatusParam = new URLSearchParams(window.location.search).get(
    'status',
  );

  /** Orders currently selected from the store (all, or active-only per the `status` query param). */
  private storeOrders = toSignal(
    this.store.select(
      this.ordersStatusParam === OrderStatus.Active
        ? fromStore.getActiveOrders
        : fromStore.getAllOrders,
    ),
    { initialValue: [] as Order[] },
  );

  /** storeOrders narrowed by the search box. This is what the template renders. */
  filteredOrders = computed(() =>
    this.applySearch(this.storeOrders(), this.searchTerm()),
  );

  isLoading$: Observable<boolean> = this.store.select(
    fromStore.getOrdersLoading,
  );

  isLoaded$: Observable<boolean> = this.store.select(fromStore.getOrdersLoaded);

  detailView$: Observable<string> = this.store.select(
    fromStore.getOrderDetailView,
  );

  selectedOrderId$: Observable<string> = this.store.select(
    fromStore.getSelectedOrderId,
  );

  selectedLineItemId$: Observable<string> = this.store.select(
    fromStore.getSelectedLineItemId,
  );

  browserSize$: Observable<string> = this.store.select(
    fromStore.getBrowserSizeName,
  );

  viewsObject = views;

  formattedCSVData = computed(() => this.buildCSVRows(this.storeOrders()));
  tableCsvData = signal<OrderCsvDataRow[]>([]);

  csvFileName = 'Orders-Status';

  searchTermControl = new UntypedFormControl();

  private searchTerm = toSignal(
    this.searchTermControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map((value: string) =>
        (value === 'Pending' ? 'New' : (value ?? '')).trim().toLowerCase(),
      ),
      distinctUntilChanged(),
    ),
    { initialValue: '' },
  );

  page: number = 1;

  currentView = signal<'summary' | 'table'>('summary');

  /** Table "View more" detail is open — hide Orders title + Summary/Table toggle row. */
  tableViewDetailActive = signal(false);

  constructor() {
    effect(() => {
      const orders = this.storeOrders();
      if (orders.length === 0) {
        return;
      }
      const initialOrderDetail: DisplayOrder = {
        detailView: views.order,
        orderId: orders[0].Id,
      };
      this.store.dispatch(new fromStore.DisplayOrderDetail(initialOrderDetail));
      this.store.dispatch(
        new fromStore.SetSelectedSOF(initialOrderDetail.orderId),
      );
    });
  }

  ngOnInit(): void {
    const view = this.route.snapshot.queryParamMap.get('view');
    const viewMap: Record<string, 'summary' | 'table'> = {
      table: 'table',
      list: 'table',
      status: 'summary',
    };
    this.currentView.set(viewMap[view] ?? 'summary');

    this.store
      .select(fromStore.getOrdersLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadOrders());
        }
      });

    this.store
      .select(fromStore.getSOFLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadServiceOrderForms());
        }
      });

    // Set search term from URL param if present
    const searchParams = new URLSearchParams(window.location.search);
    const services = searchParams.get('services');
    if (services) {
      this.searchTermControl.setValue(services);
    }
  }

  private applySearch(orders: Order[], searchTerm: string): Order[] {
    const fields = [
      'Name',
      'Status__c',
      'Owner_Name__c',
      'Account__r.Name',
      'Sales_Rep__r.Name',
      'Sales_Rep__c.Name',
      'Account_Manager__r.Name',
      'Customer_Order_OLI__r.records.Service_Type__c',
      'Customer_Order_OLI__r.records.Line_Item_Type__c',
      'Customer_Order_OLI__r.records.Term__c',
      'Customer_Order_OLI__r.records.Service__r.Name',
      'Customer_Order_OLI__r.records.Service__r.Status__c',
      'Customer_Order_OLI__r.records.Service__r.Vendor__r.Name',
      'Customer_Order_OLI__r.records.Service_Order2__r.Name',
      'Customer_Order_OLI__r.records.Location__r.Name',
      'Customer_Order_OLI__r.records.Service_Order2__r.Status__c',
      'Customer_Order_OLI__r.records.Service_Order2__r.Customer_PON__c',
      'Customer_Order_OLI__r.records.Service_Order2__r.Owner_Name__c',
    ];
    if (!searchTerm) {
      return orders;
    }
    return orders.filter((order) =>
      objectContainsTerm(order, searchTerm, fields),
    );
  }

  onTableViewDetailActive(active: boolean): void {
    this.tableViewDetailActive.set(active);
  }

  onTableCsvDataChange(data: OrderCsvDataRow[]): void {
    this.tableCsvData.set(data);
  }

  setView(view: 'summary' | 'table'): void {
    this.currentView.set(view);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onSelect(event: DisplayOrder): void {
    this.store.dispatch(new fromStore.DisplayOrderDetail(event));
    this.store.dispatch(new fromStore.SetSelectedSOF(event.orderId));
  }

  private buildCSVRows(orders: Order[]): any[] {
    const newCSVData = [];

    orders.forEach((order) => {
      const dealOwner = order.Owner_Name__c;

      if (order.Customer_Order_OLI__r) {
        // eslint-disable-next-line prettier/prettier
        order.Customer_Order_OLI__r.records.forEach((lineItem) => {
          let orderStatus = NOT_AVAILABLE;
          let completedDate = NOT_AVAILABLE;
          let accountName = '';
          let elementInfo: any = {};
          let lastComment = '';
          let lastCommentDate = null;
          let sordOwner = '';

          if (lineItem.Account__c) {
            accountName = lineItem?.Account__r?.Name;
          } else if (order.Account__c) {
            accountName = order.Account__r?.Name;
          }

          if (lineItem.Service_Order2__r) {
            orderStatus =
              lineItem.Service_Order2__r.Status__c === 'Ready'
                ? 'Pending'
                : lineItem.Service_Order2__r.Status__c;
            completedDate = lineItem.Service_Order2__r.Completed_Date__c
              ? moment(lineItem.Service_Order2__r.Completed_Date__c).format('L')
              : '';

            sordOwner = lineItem.Service_Order2__r.Owner_Name__c || '';

            lastComment = (
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Order_Comments__r &&
              lineItem.Service_Order2__r.Order_Comments__r.length > 0 &&
              lineItem.Service_Order2__r.Order_Comments__r[0]
                ? lineItem.Service_Order2__r.Order_Comments__r[0].Body__c
                : ''
            ).replace(/<[^>]*>?/gm, '');

            lastCommentDate =
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Order_Comments__r &&
              lineItem.Service_Order2__r.Order_Comments__r.length > 0 &&
              lineItem.Service_Order2__r.Order_Comments__r[0]
                ? moment(
                    lineItem.Service_Order2__r.Order_Comments__r[0].CreatedDate,
                  ).format('L')
                : null;

            if (lineItem.Service__r) {
              elementInfo = this.csvFormatter.getElementInfo(
                lineItem.Service__r,
              );
            }
          }
          const locationInfo = {
            streetAddress: '',
            city: '',
            state: '',
            zip: '',
          };
          if (lineItem.Location__r !== null) {
            let street1 = '';
            let street2 = '';
            if (lineItem.Location__r.Street_1__c !== null) {
              street1 = lineItem.Location__r.Street_1__c;
            }
            if (lineItem.Location__r.Street_2__c !== null) {
              street2 = lineItem.Location__r.Street_2__c;
            }
            locationInfo.streetAddress = `${street1} ${street2}`.trim();
            locationInfo.city = lineItem.Location__r.City__c;
            locationInfo.state = lineItem.Location__r.State__c;
            locationInfo.zip = lineItem.Location__r.Zip_Code__c;
          }

          const object = {
            NIT: lineItem.Service__r ? lineItem.Service__r.Name : '',
            'Service Status': lineItem.Service__r
              ? lineItem.Service__r?.Status__c
              : '',
            'Customer Site ID': lineItem.Service__r
              ? lineItem.Service__r.Z_Location_Customer_Side_Id__c
              : '',
            'Customer PON':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Customer_PON__c
                ? lineItem.Service_Order2__r.Customer_PON__c
                : '',
            'Deal Number': order.Name ? order.Name : '',
            'SORD Number':
              lineItem.Service_Order2__r && lineItem.Service_Order2__r.Name
                ? lineItem.Service_Order2__r.Name
                : '',
            'Deal Status': order?.Status__c ? order?.Status__c : '',
            Customer: accountName,
            'End User Company':
              lineItem?.Service__r?.Z_Location_Company_Name__c,
            'Street Address': locationInfo.streetAddress,
            City: locationInfo.city,
            State: locationInfo.state,
            ZIP: locationInfo.zip,
            Provider: (() => {
              if (lineItem?.Service__r?.Vendor__r?.Display_Alias__c) {
                return lineItem.Service__r.Vendor__r.Display_Alias__c;
              }
              if (lineItem?.Service__r?.Vendor__r?.Name) {
                return lineItem.Service__r.Vendor__r.Name;
              }
              return '';
            })(),
            'Local Contact': lineItem.LocalContact,
            'Order Type': lineItem.RecordType.Name
              ? lineItem.RecordType.Name
              : '',
            'Order Status': orderStatus,
            MRC: lineItem?.Service__r?.MRR__c
              ? `$${lineItem.Service__r.MRR__c}`
              : '$0.00',
            'Sales Representative': order.Sales_Rep__r
              ? `${order.Sales_Rep__r.LastName}, ${order.Sales_Rep__r.FirstName}`
              : '',
            'Account Manager': order.Account_Manager__r
              ? `${order.Account_Manager__r.LastName}, ${order.Account_Manager__r.FirstName}`
              : '',
            'Client Project Manager': order.Default_Task_Assignments__r
              ? `${order.Default_Task_Assignments__r.records[0].User__r.LastName}, ${order.Default_Task_Assignments__r.records[0].User__r.FirstName}`
              : '',
            'Deal Owner': dealOwner,
            'Order Owner': sordOwner,
            'Bundle Type': lineItem.Service_Type__c
              ? lineItem.Service_Type__c
              : '',
            Term: lineItem.Term__c,
            'Loop CIR': lineItem?.Service__r?.Asymmetrical_Bandwidth__c
              ? `${lineItem?.Service__r?.Bandwidth_Downstream__c} / ${lineItem.Service__r.Bandwidth_Upstream__c}`
              : lineItem.Service__r?.Bandwidth_Downstream__c,
            'Circuit Hand-Off':
              lineItem?.Service__r?.Connector_Type_Z_Location__c,
            'Provider Circuit ID': elementInfo.vendorCircuitId
              ? elementInfo.vendorCircuitId
              : '',
            'WAN IP Block': elementInfo.wanIpBlock
              ? elementInfo.wanIpBlock
              : '',
            'WAN Mask': elementInfo.wanMask ? elementInfo.wanMask : '',
            'LAN IP Block': elementInfo.lanIpBlock
              ? elementInfo.lanIpBlock
              : '',
            'LAN Mask': elementInfo.lanMask ? elementInfo.lanMask : '',
            Dmarc: elementInfo.dmarcZLoc ? elementInfo.dmarcZLoc : '',
            'Nitel Equipment': elementInfo.equipmentName
              ? elementInfo.equipmentName
              : '',
            'Tracking Number':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Equipment_Shipment_Tracking_Number__c
                ? lineItem.Service_Order2__r
                    .Equipment_Shipment_Tracking_Number__c
                : '',
            'Shipping Carrier':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Shipping_Carrier__c
                ? lineItem.Service_Order2__r.Shipping_Carrier__c
                : '',
            'Order Date': order.Order_Date__c ? order.Order_Date__c : '',
            'Provider Firm Order Confirmation Date':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Vendor_FOC_Date__c
                ? lineItem.Service_Order2__r.Vendor_FOC_Date__c
                : '',
            'Nitel Firm Order Confirmation Date':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.Nitel_FOC_Date__c
                ? lineItem.Service_Order2__r.Nitel_FOC_Date__c
                : '',
            'Order Completed Date': completedDate,
            'Start Of Service Date': lineItem?.Service__r?.Start_Of_Service__c
              ? moment(lineItem.Service__r.Start_Of_Service__c).format('L')
              : '',
            'Scheduled Activation Date': lineItem.ScheduledActivationDate
              ? moment(lineItem.ScheduledActivationDate).format('L')
              : '',
            'Activation Completed Date': lineItem?.Service__r
              ?.Activation_Date__c
              ? moment(lineItem.Service__r.Activation_Date__c).format('L')
              : '',
            'Billing Initiated Date': lineItem?.Service__r?.First_Invoice__c
              ? moment(lineItem.Service__r.First_Invoice__c).format('L')
              : '',
            'End Of Service Date': lineItem?.Service__r?.End_Of_Service__c
              ? moment(lineItem.Service__r.End_Of_Service__c).format('L')
              : '',
            'Jeopardy Code':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.activeJeopardy
                ? lineItem.Service_Order2__r.activeJeopardy.Jeopardy_Code__c
                : '',
            'Jeopardy Reason':
              lineItem.Service_Order2__r &&
              lineItem.Service_Order2__r.activeJeopardy
                ? lineItem.Service_Order2__r.activeJeopardy.Jeopardy_Reason__c
                : '',
            'Is Staggered': lineItem.Service_Order2__r
              ? lineItem.Service_Order2__r.In_Staggered_Order_Jeopardy__c
              : false,
            'Most Recent Comment': lastComment,
            'Most Recent Comment Date': lastCommentDate,
          };
          newCSVData.push(object);
        });
      }
    });

    return newCSVData;
  }
}
